import React, { useState, useEffect } from 'react';
import './TradingJournal.css';
import { getETNow, getETDateString } from './utils/marketUtils';
import {
  listTradeRecords,
  getTradeRecord,
  deleteTradeRecord as apiDeleteTradeRecord,
  saveTradeRecord as apiSaveTradeRecord,
  saveEnrichedTrade as apiSaveEnrichedTrade,
  listBrokerImports
} from './services/apiService';
import {
  matchRecords,
  calculateProfitLoss,
  analyzeAIAccuracy,
  generateStats
} from './services/enrichmentService';
import TradeChartView from './TradeChartView';

/**
 * Trading Journal - Trade Decision Log
 * Shows all trading decisions recorded via Entry/Exit buttons
 */
const TradingJournal = ({ onClose }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('today'); // 'today', 'week', 'month', 'all'
  const [editingRecord, setEditingRecord] = useState(null); // { record, editedValues }
  const [deletingRecord, setDeletingRecord] = useState(null); // record to delete

  // Search, Sort, Filter state
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState('time-desc'); // 'time-desc', 'time-asc', 'symbol', 'price-high', 'price-low'
  const [filterAction, setFilterAction] = useState('all'); // 'all', 'entry', 'exit'
  const [filterAI, setFilterAI] = useState(false); // only show AI recommended
  const [filterLinked, setFilterLinked] = useState('all'); // 'all', 'linked', 'unlinked'

  // Custom date range state
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Auto-match state
  const [showMatchDialog, setShowMatchDialog] = useState(false);
  const [matchResult, setMatchResult] = useState(null);
  const [matching, setMatching] = useState(false);
  const [expandedPairs, setExpandedPairs] = useState(false);
  const [expandedUnmatched, setExpandedUnmatched] = useState(false);
  const [pairsSortBy, setPairsSortBy] = useState('profit-desc'); // 'profit-desc', 'profit-asc', 'time-desc', 'time-asc', 'symbol', 'percent-desc', 'duration'
  const [forceRematch, setForceRematch] = useState(false);
  const [applyingMatches, setApplyingMatches] = useState(false);
  const [applyProgress, setApplyProgress] = useState({ current: 0, total: 0, step: '' });

  // View mode state (list or chart)
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'chart'

  useEffect(() => {
    loadRecords();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRecords = async () => {
    setLoading(true);
    try {
      await loadFromApi();
    } catch (error) {
      console.error('Failed to load records:', error);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const loadFromApi = async () => {
    const allRecords = [];

    try {
      // Load all trade records from API (wide date range to capture all)
      const startDate = '2020-01-01';
      const endDate = getETDateString(); // YYYY-MM-DD in ET
      const resp = await listTradeRecords(startDate, endDate);
      const items = Array.isArray(resp) ? resp : [];

      for (const item of items) {
        const data = typeof item.data === 'string' ? JSON.parse(item.data) : item.data;
        if (data?.trades && Array.isArray(data.trades)) {
          // Ensure each trade has dateOnly from the parent record's date
          const itemDate = item.date;
          data.trades.forEach(t => {
            if (!t.dateOnly && itemDate) t.dateOnly = itemDate;
          });
          allRecords.push(...data.trades);
        }
      }

      // Sort by time descending
      allRecords.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setRecords(allRecords);
    } catch (error) {
      console.error('Error loading from API:', error);
      setRecords([]);
    }
  };

  // Filter and sort records
  const getFilteredRecords = () => {
    const now = getETNow();
    const today = getETDateString();

    // 1. Filter by time range
    let filtered = records;
    switch (selectedTab) {
      case 'today':
        filtered = records.filter(r => r.dateOnly === today);
        break;
      case 'week':
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        filtered = records.filter(r => new Date(r.timestamp) >= weekAgo);
        break;
      case 'month':
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        filtered = records.filter(r => new Date(r.timestamp) >= monthAgo);
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          const start = new Date(customStartDate);
          const end = new Date(customEndDate);
          end.setHours(23, 59, 59, 999); // Include entire end day
          filtered = records.filter(r => {
            const recordDate = new Date(r.timestamp);
            return recordDate >= start && recordDate <= end;
          });
        }
        break;
      case 'all':
      default:
        filtered = records;
    }

    // 2. Search filter (symbol or notes)
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(r =>
        r.symbol.toLowerCase().includes(searchLower) ||
        (r.notes && r.notes.toLowerCase().includes(searchLower))
      );
    }

    // 3. Action filter
    if (filterAction !== 'all') {
      filtered = filtered.filter(r => r.action === filterAction);
    }

    // 4. AI recommendation filter
    if (filterAI) {
      filtered = filtered.filter(r =>
        r.aiRecommendations?.claudeRecommended || r.aiRecommendations?.gptRecommended
      );
    }

    // 5. Linked status filter
    if (filterLinked === 'linked') {
      filtered = filtered.filter(r => r.brokerRecordLinked);
    } else if (filterLinked === 'unlinked') {
      filtered = filtered.filter(r => !r.brokerRecordLinked);
    }

    // 6. Sort
    filtered = [...filtered];
    switch (sortBy) {
      case 'time-desc':
        filtered.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        break;
      case 'time-asc':
        filtered.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        break;
      case 'symbol':
        filtered.sort((a, b) => a.symbol.localeCompare(b.symbol));
        break;
      case 'price-high':
        filtered.sort((a, b) => b.actualPrice - a.actualPrice);
        break;
      case 'price-low':
        filtered.sort((a, b) => a.actualPrice - b.actualPrice);
        break;
      default:
        break;
    }

    return filtered;
  };

  // Group by date
  const groupByDate = (records) => {
    const groups = {};
    records.forEach(record => {
      const date = record.dateOnly;
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(record);
    });
    return groups;
  };

  // Delete record
  const handleDeleteRecord = async (record) => {
    try {
      await deleteRecordViaApi(record);
      await loadRecords();
      setDeletingRecord(null);
    } catch (error) {
      console.error('Failed to delete record:', error);
      alert(`Failed to delete: ${error.message}`);
    }
  };

  const deleteRecordViaApi = async (record) => {
    const date = record.dateOnly;

    // Read existing day data from API
    let dayData;
    try {
      const resp = await getTradeRecord(date);
      dayData = resp?.data ? (typeof resp.data === 'string' ? JSON.parse(resp.data) : resp.data) : null;
    } catch {
      dayData = null;
    }

    if (!dayData || !dayData.trades) {
      throw new Error('Record not found');
    }

    // Filter out the record to delete
    dayData.trades = dayData.trades.filter(r => r.id !== record.id);

    // Delete the entire DB row if no trades left, otherwise save updated data
    if (dayData.trades.length === 0) {
      await apiDeleteTradeRecord(date);
    } else {
      await apiSaveTradeRecord(date, dayData);
    }
  };

  // Edit record
  const handleEditRecord = (record) => {
    setEditingRecord({
      original: record,
      edited: {
        actualPrice: record.actualPrice,
        quantity: record.quantity,
        notes: record.notes || ''
      }
    });
  };

  const handleSaveEdit = async () => {
    try {
      const { original, edited } = editingRecord;

      // Update record
      const updatedRecord = {
        ...original,
        actualPrice: parseFloat(edited.actualPrice),
        quantity: parseInt(edited.quantity),
        notes: edited.notes.trim()
      };

      await updateRecordViaApi(updatedRecord);
      await loadRecords();
      setEditingRecord(null);
    } catch (error) {
      console.error('Failed to save edit:', error);
      alert(`Failed to save: ${error.message}`);
    }
  };

  const updateRecordViaApi = async (record) => {
    const date = record.dateOnly;

    // Read existing day data from API
    let dayData;
    try {
      const resp = await getTradeRecord(date);
      dayData = resp?.data ? (typeof resp.data === 'string' ? JSON.parse(resp.data) : resp.data) : null;
    } catch {
      dayData = null;
    }

    if (!dayData || !dayData.trades) {
      throw new Error('Record not found');
    }

    // Update the record
    dayData.trades = dayData.trades.map(r =>
      r.id === record.id ? record : r
    );

    // Save
    await apiSaveTradeRecord(date, dayData);
  };

  // Auto-match functionality
  const handleAutoMatch = async () => {
    // Confirm dialog: if Force Re-match is checked and there are linked records
    if (forceRematch) {
      const linkedCount = records.filter(r => r.brokerRecordLinked).length;
      if (linkedCount > 0) {
        const confirmed = window.confirm(
          `You have ${linkedCount} linked record${linkedCount > 1 ? 's' : ''}. ` +
          `Force re-matching will reprocess all records.\n\n` +
          `Continue?`
        );
        if (!confirmed) {
          return;
        }
      }
    }

    setMatching(true);

    try {
      // Load broker data from API
      const brokerTrades = await loadBrokerDataFromApi();

      if (brokerTrades.length === 0) {
        alert('No broker data found. Please import broker data first.');
        setMatching(false);
        return;
      }

      // Prepare records: if forceRematch, temporarily remove brokerRecordLinked flag
      const recordsForMatching = forceRematch
        ? records.map(r => ({ ...r, brokerRecordLinked: false }))
        : records;

      // Execute matching
      const result = matchRecords(recordsForMatching, brokerTrades);

      // Calculate P/L for paired trades
      const { pairs, unmatchedEntries, unmatchedExits } = calculateProfitLoss(result.matches);

      // Analyze AI accuracy
      const aiAnalysis = analyzeAIAccuracy(pairs);

      // Generate stats
      const stats = generateStats(result.matches, pairs, result.unmatchedUI);

      // Save results
      setMatchResult({
        ...result,
        pairs,
        unmatchedEntries,
        unmatchedExits,
        aiAnalysis,
        stats
      });

      // Reset expanded states and sorting
      setExpandedPairs(false);
      setExpandedUnmatched(false);
      setPairsSortBy('profit-desc');

      setShowMatchDialog(true);
    } catch (error) {
      console.error('Auto-match failed:', error);
      alert(`Auto-match failed: ${error.message}`);
    } finally {
      setMatching(false);
    }
  };

  // Load broker data from API
  const loadBrokerDataFromApi = async () => {
    const allTrades = [];

    try {
      const imports = await listBrokerImports();
      const items = Array.isArray(imports) ? imports : [];

      for (const item of items) {
        const data = typeof item.data === 'string' ? JSON.parse(item.data) : item.data;
        if (data?.transactions && Array.isArray(data.transactions)) {
          allTrades.push(...data.transactions);
        }
      }
    } catch (error) {
      console.error('Failed to load broker data:', error);
    }

    return allTrades;
  };

  // Sort paired trades
  const sortPairs = (pairs, sortBy) => {
    const sorted = [...pairs];

    switch (sortBy) {
      case 'profit-desc':
        sorted.sort((a, b) => b.profit - a.profit);
        break;
      case 'profit-asc':
        sorted.sort((a, b) => a.profit - b.profit);
        break;
      case 'percent-desc':
        sorted.sort((a, b) => b.profitPercent - a.profitPercent);
        break;
      case 'percent-asc':
        sorted.sort((a, b) => a.profitPercent - b.profitPercent);
        break;
      case 'time-desc':
        sorted.sort((a, b) => new Date(b.exit.uiRecord.timestamp) - new Date(a.exit.uiRecord.timestamp));
        break;
      case 'time-asc':
        sorted.sort((a, b) => new Date(a.exit.uiRecord.timestamp) - new Date(b.exit.uiRecord.timestamp));
        break;
      case 'symbol':
        sorted.sort((a, b) => a.symbol.localeCompare(b.symbol));
        break;
      case 'duration':
        // Parse duration string (e.g., "2h 30m" or "45m") to minutes
        const getDurationMinutes = (durationStr) => {
          const hours = durationStr.match(/(\d+)h/);
          const minutes = durationStr.match(/(\d+)m/);
          return (hours ? parseInt(hours[1]) * 60 : 0) + (minutes ? parseInt(minutes[1]) : 0);
        };
        sorted.sort((a, b) => getDurationMinutes(b.duration) - getDurationMinutes(a.duration));
        break;
      default:
        break;
    }

    return sorted;
  };

  // Apply match results (update records as linked)
  const applyMatches = async () => {
    if (!matchResult) return;

    setApplyingMatches(true);
    const totalSteps = matchResult.matches.length + 2; // matches + enriched + reload
    let currentStep = 0;

    try {
      // 1. Update all matched records (mark as linked)
      for (let i = 0; i < matchResult.matches.length; i++) {
        const match = matchResult.matches[i];
        const updatedRecord = {
          ...match.uiRecord,
          brokerRecordLinked: true,
          brokerData: {
            price: match.brokerPrice,
            slippage: match.slippage,
            fees: match.fees,
            matchedAt: new Date().toISOString()
          }
        };

        await updateRecordViaApi(updatedRecord);

        // Update progress
        if (i % 5 === 0 || i === matchResult.matches.length - 1) {
          currentStep = i + 1;
          setApplyProgress({
            current: currentStep,
            total: totalSteps,
            step: ''
          });
        }
      }

      // 2. Save enriched data
      currentStep = matchResult.matches.length + 1;
      setApplyProgress({ current: currentStep, total: totalSteps, step: '' });
      await saveEnrichedDataViaApi(matchResult);

      // 3. Reload records
      currentStep = matchResult.matches.length + 2;
      setApplyProgress({ current: currentStep, total: totalSteps, step: '' });
      await loadRecords();

      setShowMatchDialog(false);
      setApplyingMatches(false);
      alert('Matches applied successfully!');
    } catch (error) {
      console.error('Failed to apply matches:', error);
      setApplyingMatches(false);
      alert(`Failed to apply: ${error.message}`);
    }
  };

  // Save enriched data via API
  const saveEnrichedDataViaApi = async (matchResult) => {
    try {
      // Group pairs by date
      const pairsByDate = {};
      matchResult.pairs.forEach(pair => {
        let entryDate = pair.entry.uiRecord.dateOnly;
        if (!entryDate) {
          entryDate = new Date(pair.entry.uiRecord.timestamp).toISOString().split('T')[0];
        }

        if (!pairsByDate[entryDate]) {
          pairsByDate[entryDate] = [];
        }
        pairsByDate[entryDate].push(pair);
      });

      // Save each date's enriched data
      for (const [dateStr, dayPairs] of Object.entries(pairsByDate)) {
        // Build enriched data for this date
        const enrichedData = {
          date: dateStr,
          enrichedPairs: dayPairs,
          stats: {
            totalPairs: dayPairs.length,
            totalProfit: dayPairs.reduce((sum, p) => sum + p.profit, 0),
            wins: dayPairs.filter(p => p.profit > 0).length,
            losses: dayPairs.filter(p => p.profit < 0).length,
            winRate: dayPairs.length > 0
              ? ((dayPairs.filter(p => p.profit > 0).length / dayPairs.length) * 100).toFixed(1)
              : 0,
            avgProfit: dayPairs.length > 0
              ? (dayPairs.reduce((sum, p) => sum + p.profit, 0) / dayPairs.length).toFixed(2)
              : 0
          },
          aiAnalysis: matchResult.aiAnalysis || null
        };

        await apiSaveEnrichedTrade(dateStr, enrichedData);
        console.log(`Saved enriched data: ${dateStr}`);
      }
    } catch (error) {
      console.error('Failed to save enriched data:', error);
      throw error;
    }
  };

  // Calculate stats
  const calculateStats = (records) => {
    const total = records.length;
    const entries = records.filter(r => r.action === 'entry').length;
    const exits = records.filter(r => r.action === 'exit').length;
    const aiRecommended = records.filter(r =>
      r.aiRecommendations?.claudeRecommended || r.aiRecommendations?.gptRecommended
    ).length;
    const bothAI = records.filter(r =>
      r.aiRecommendations?.claudeRecommended && r.aiRecommendations?.gptRecommended
    ).length;

    return { total, entries, exits, aiRecommended, bothAI };
  };

  // Render star ratings (1-3 scale)
  const renderStars = (rating) => {
    if (!rating) return '';
    return '\u2B50'.repeat(Math.min(rating, 3));
  };

  // Format time
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  // Format date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = getETDateString();
    const etNow = getETNow();
    const yesterday = new Date(etNow.getTime() - 86400000).toLocaleDateString('en-CA');

    if (dateStr === today) return 'Today';
    if (dateStr === yesterday) return 'Yesterday';

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      weekday: 'short'
    });
  };

  const filteredRecords = getFilteredRecords();
  const groupedRecords = groupByDate(filteredRecords);
  const stats = calculateStats(filteredRecords);

  if (loading) {
    return (
      <div className="journal-overlay">
        <div className="journal-dialog">
          <div className="journal-loading">
            Loading trade records...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="journal-overlay" onClick={onClose}>
      <div className="journal-dialog" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="journal-header">
          <h2>Trade Log</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Time Range Tabs + View Toggle */}
        <div className="journal-tabs-row">
          <div className="journal-tabs">
            <button
              className={selectedTab === 'today' ? 'active' : ''}
              onClick={() => setSelectedTab('today')}
            >
              Today
            </button>
            <button
              className={selectedTab === 'week' ? 'active' : ''}
              onClick={() => setSelectedTab('week')}
            >
              This Week
            </button>
            <button
              className={selectedTab === 'month' ? 'active' : ''}
              onClick={() => setSelectedTab('month')}
            >
              This Month
            </button>
            <button
              className={selectedTab === 'all' ? 'active' : ''}
              onClick={() => setSelectedTab('all')}
            >
              All
            </button>
            <button
              className={selectedTab === 'custom' ? 'active' : ''}
              onClick={() => setSelectedTab('custom')}
            >
              Custom
            </button>
            {selectedTab === 'custom' && (
              <div className="custom-date-range">
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                />
                <span className="date-separator">to</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                />
              </div>
            )}
          </div>

          {/* View Toggle */}
          <div className="view-toggle">
            <button
              className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List View"
            >
              List
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'chart' ? 'active' : ''}`}
              onClick={() => setViewMode('chart')}
              title="Chart View"
            >
              Chart
            </button>
          </div>
        </div>

        {/* Search, Sort, Filter Toolbar */}
        <div className="journal-toolbar">
          <div className="toolbar-row">
            {/* Search */}
            <div className="search-box">
              <input
                type="text"
                placeholder="Search symbol or notes..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="search-input"
              />
              {searchText && (
                <button
                  className="clear-search"
                  onClick={() => setSearchText('')}
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Sort */}
            <div className="sort-box">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="sort-select"
              >
                <option value="time-asc">Oldest First</option>
                <option value="time-desc">Newest First</option>
                <option value="symbol">Symbol A-Z</option>
                <option value="price-high">Price High-Low</option>
                <option value="price-low">Price Low-High</option>
              </select>
            </div>
          </div>

          <div className="toolbar-row filters">
            {/* Action Filter */}
            <div className="filter-group">
              <span className="filter-label">Action:</span>
              <button
                className={`filter-btn ${filterAction === 'all' ? 'active' : ''}`}
                onClick={() => setFilterAction('all')}
              >
                All
              </button>
              <button
                className={`filter-btn entry ${filterAction === 'entry' ? 'active' : ''}`}
                onClick={() => setFilterAction('entry')}
              >
                Entry
              </button>
              <button
                className={`filter-btn exit ${filterAction === 'exit' ? 'active' : ''}`}
                onClick={() => setFilterAction('exit')}
              >
                Exit
              </button>
            </div>

            <span className="filter-separator">•</span>

            {/* AI Filter */}
            <div className="filter-group">
              <span className="filter-label">AI:</span>
              <button
                className={`filter-btn ${filterAI ? 'active' : ''}`}
                onClick={() => setFilterAI(!filterAI)}
              >
                {filterAI ? 'On' : 'Off'}
              </button>
            </div>

            <span className="filter-separator">•</span>

            {/* Linked Filter */}
            <div className="filter-group">
              <span className="filter-label">Status:</span>
              <button
                className={`filter-btn ${filterLinked === 'all' ? 'active' : ''}`}
                onClick={() => setFilterLinked('all')}
              >
                All
              </button>
              <button
                className={`filter-btn ${filterLinked === 'linked' ? 'active' : ''}`}
                onClick={() => setFilterLinked('linked')}
              >
                Linked
              </button>
              <button
                className={`filter-btn ${filterLinked === 'unlinked' ? 'active' : ''}`}
                onClick={() => setFilterLinked('unlinked')}
              >
                Unlinked
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="journal-stats">
          <div className="stat-item">
            <span className="stat-label">Total Decisions</span>
            <span className="stat-value">{stats.total}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Entries</span>
            <span className="stat-value entry">{stats.entries}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Exits</span>
            <span className="stat-value exit">{stats.exits}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">AI Recommended</span>
            <span className="stat-value ai">{stats.aiRecommended}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Both AI</span>
            <span className="stat-value both-ai">{stats.bothAI}</span>
          </div>
        </div>

        {/* Content Area - List or Chart View */}
        <div className="journal-content">
          {viewMode === 'chart' ? (
            /* Chart View */
            <TradeChartView
              records={filteredRecords}
              pairs={matchResult?.pairs || []}
            />
          ) : filteredRecords.length === 0 ? (
            <div className="empty-state">
              <p>No trade decisions recorded yet.</p>
              <p className="hint">Click Entry/Exit buttons in the stock table to record your trading decisions.</p>
            </div>
          ) : (
            /* List View */
            Object.entries(groupedRecords).map(([date, dayRecords]) => (
              <div key={date} className="day-group">
                <div className="day-header">
                  <span className="day-label">{formatDate(date)}</span>
                  <span className="day-date">{date}</span>
                </div>

                {dayRecords.map(record => (
                  <div key={record.id} className={`record-card ${record.action}`}>
                    {/* Record Header */}
                    <div className="record-header">
                      <div className="record-title">
                        <span className={`action-badge ${record.action}`}>
                          {record.action === 'entry' ? 'ENTRY' : 'EXIT'}
                        </span>
                        <span className="symbol">{record.symbol}</span>
                        <span className="price">${(record.actualPrice ?? 0).toFixed(2)}</span>
                        <span className="quantity">x{record.quantity}</span>
                      </div>
                      <div className="record-actions">
                        <span className="time">{formatTime(record.timestamp)}</span>
                        <button
                          className="action-btn edit-btn"
                          onClick={() => handleEditRecord(record)}
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          className="action-btn delete-btn"
                          onClick={() => setDeletingRecord(record)}
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>

                    {/* AI Recommendations */}
                    {(record.aiRecommendations?.claudeRecommended || record.aiRecommendations?.gptRecommended) && (
                      <div className="ai-badges">
                        {record.aiRecommendations.claudeRecommended && (
                          <span className="ai-badge claude">
                            Claude {renderStars(record.aiRecommendations.claudeRating)}
                          </span>
                        )}
                        {record.aiRecommendations.gptRecommended && (
                          <span className="ai-badge gpt">
                            GPT {renderStars(record.aiRecommendations.gptRating)}
                          </span>
                        )}
                        {!record.aiRecommendations.claudeRecommended && !record.aiRecommendations.gptRecommended && (
                          <span className="ai-badge none">No AI Recommendation</span>
                        )}
                      </div>
                    )}

                    {/* Market Snapshot */}
                    <div className="snapshot">
                      <div className="snapshot-row">
                        <span className="snapshot-item">
                          Score: <strong>{record.snapshot?.score?.toFixed(0) || 'N/A'}</strong>
                        </span>
                        <span className="snapshot-item">
                          Daily: <strong className={record.snapshot?.dailyGain >= 0 ? 'positive' : 'negative'}>
                            {record.snapshot?.dailyGain?.toFixed(2)}%
                          </strong>
                        </span>
                        <span className="snapshot-item">
                          Avg: <strong className={record.snapshot?.avgChange >= 0 ? 'positive' : 'negative'}>
                            {record.snapshot?.avgChange?.toFixed(2)}%
                          </strong>
                        </span>
                      </div>
                      <div className="snapshot-row">
                        <span className="snapshot-item">
                          SPY: {record.snapshot?.indices?.SPY?.toFixed(2)}%
                        </span>
                        <span className="snapshot-item">
                          QQQ: {record.snapshot?.indices?.QQQ?.toFixed(2)}%
                        </span>
                        <span className="snapshot-item">
                          DIA: {record.snapshot?.indices?.DIA?.toFixed(2)}%
                        </span>
                        <span className="snapshot-item">
                          IWM: {record.snapshot?.indices?.IWM?.toFixed(2)}%
                        </span>
                      </div>
                    </div>

                    {/* Notes */}
                    {record.notes && (
                      <div className="notes">
                        <strong>Notes:</strong> {record.notes}
                      </div>
                    )}

                    {/* Broker Status */}
                    <div className="broker-status">
                      {record.brokerRecordLinked ? (
                        <span className="status-badge linked" title="This decision is linked to actual broker transaction">
                          Linked to Broker
                        </span>
                      ) : (
                        <span className="status-badge unlinked" title="This decision is not yet linked to broker data. Use auto-match or manual linking to verify execution.">
                          Pending Verification
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="journal-footer">
          <div className="footer-left">
            <label className="force-rematch-checkbox">
              <input
                type="checkbox"
                checked={forceRematch}
                onChange={(e) => setForceRematch(e.target.checked)}
              />
              <span>Force Re-match (re-process all records, including linked ones)</span>
            </label>
          </div>
          <div className="footer-actions">
            <button
              className="btn-primary"
              onClick={loadRecords}
              title="Refresh records"
            >
              Refresh
            </button>
            <button
              className="btn-primary"
              onClick={handleAutoMatch}
              disabled={matching || records.length === 0}
              title={matching ? `Processing ${forceRematch ? records.length : records.filter(r => !r.brokerRecordLinked).length} records` : 'Match UI records with broker data'}
            >
              {matching
                ? `Matching (${forceRematch ? records.length : records.filter(r => !r.brokerRecordLinked).length})...`
                : 'Auto-Match'
              }
            </button>
          </div>
        </div>
      </div>

      {/* Edit Dialog */}
      {editingRecord && (
        <div className="modal-overlay" onClick={() => setEditingRecord(null)}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Record</h3>
              <button className="close-btn" onClick={() => setEditingRecord(null)}>✕</button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label>Price *</label>
                <input
                  type="number"
                  step="0.01"
                  value={editingRecord.edited.actualPrice}
                  onChange={(e) => setEditingRecord({
                    ...editingRecord,
                    edited: { ...editingRecord.edited, actualPrice: e.target.value }
                  })}
                />
              </div>
              <div className="form-group">
                <label>Quantity *</label>
                <input
                  type="number"
                  value={editingRecord.edited.quantity}
                  onChange={(e) => setEditingRecord({
                    ...editingRecord,
                    edited: { ...editingRecord.edited, quantity: e.target.value }
                  })}
                />
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={editingRecord.edited.notes}
                  onChange={(e) => setEditingRecord({
                    ...editingRecord,
                    edited: { ...editingRecord.edited, notes: e.target.value }
                  })}
                  rows="4"
                />
              </div>

              {/* AI Analysis (Read-only) */}
              {(editingRecord.original.aiRecommendations?.claudeRecommended ||
                editingRecord.original.aiRecommendations?.gptRecommended) && (
                <div className="ai-analysis-section">
                  <h4>AI Analysis Comparison</h4>

                  {/* Table format for dual AI comparison */}
                  {editingRecord.original.aiRecommendations.claudeRecommended &&
                   editingRecord.original.aiRecommendations.gptRecommended ? (
                    <table className="ai-comparison-table">
                      <thead>
                        <tr>
                          <th className="field-column"></th>
                          <th className="claude-column">
                            Claude {renderStars(editingRecord.original.aiRecommendations.claudeRating)}
                          </th>
                          <th className="gpt-column">
                            GPT {renderStars(editingRecord.original.aiRecommendations.gptRating)}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(editingRecord.original.aiRecommendations.claudeAction ||
                          editingRecord.original.aiRecommendations.gptAction) && (
                          <tr>
                            <td className="field-label">Action</td>
                            <td className="claude-cell">
                              {editingRecord.original.aiRecommendations.claudeAction || '-'}
                            </td>
                            <td className="gpt-cell">
                              {editingRecord.original.aiRecommendations.gptAction || '-'}
                            </td>
                          </tr>
                        )}
                        {(editingRecord.original.aiRecommendations.claudeTrend ||
                          editingRecord.original.aiRecommendations.gptTrend) && (
                          <tr>
                            <td className="field-label">Trend</td>
                            <td className="claude-cell">
                              {editingRecord.original.aiRecommendations.claudeTrend || '-'}
                            </td>
                            <td className="gpt-cell">
                              {editingRecord.original.aiRecommendations.gptTrend || '-'}
                            </td>
                          </tr>
                        )}
                        {(editingRecord.original.aiRecommendations.claudePattern ||
                          editingRecord.original.aiRecommendations.gptPattern) && (
                          <tr>
                            <td className="field-label">Pattern</td>
                            <td className="claude-cell">
                              {editingRecord.original.aiRecommendations.claudePattern || '-'}
                            </td>
                            <td className="gpt-cell">
                              {editingRecord.original.aiRecommendations.gptPattern || '-'}
                            </td>
                          </tr>
                        )}
                        {(editingRecord.original.aiRecommendations.claudeMarketAnalysis ||
                          editingRecord.original.aiRecommendations.gptMarketAnalysis) && (
                          <tr>
                            <td className="field-label">Analysis</td>
                            <td className="claude-cell">
                              {editingRecord.original.aiRecommendations.claudeMarketAnalysis || '-'}
                            </td>
                            <td className="gpt-cell">
                              {editingRecord.original.aiRecommendations.gptMarketAnalysis || '-'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  ) : (
                    /* Single AI display - keep card format */
                    <div className="ai-comparison-grid">
                      {editingRecord.original.aiRecommendations.claudeRecommended && (
                        <div className="ai-detail claude-detail">
                          <div className="ai-detail-header">
                            <strong>Claude {renderStars(editingRecord.original.aiRecommendations.claudeRating)}</strong>
                          </div>
                          {editingRecord.original.aiRecommendations.claudeAction && (
                            <div className="ai-field">
                              <span className="ai-field-label">Action:</span>
                              <span>{editingRecord.original.aiRecommendations.claudeAction}</span>
                            </div>
                          )}
                          {editingRecord.original.aiRecommendations.claudeTrend && (
                            <div className="ai-field">
                              <span className="ai-field-label">Trend:</span>
                              <span>{editingRecord.original.aiRecommendations.claudeTrend}</span>
                            </div>
                          )}
                          {editingRecord.original.aiRecommendations.claudePattern && (
                            <div className="ai-field">
                              <span className="ai-field-label">Pattern:</span>
                              <span>{editingRecord.original.aiRecommendations.claudePattern}</span>
                            </div>
                          )}
                          {editingRecord.original.aiRecommendations.claudeMarketAnalysis && (
                            <div className="ai-field">
                              <span className="ai-field-label">Analysis:</span>
                              <span>{editingRecord.original.aiRecommendations.claudeMarketAnalysis}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {editingRecord.original.aiRecommendations.gptRecommended && (
                        <div className="ai-detail gpt-detail">
                          <div className="ai-detail-header">
                            <strong>GPT {renderStars(editingRecord.original.aiRecommendations.gptRating)}</strong>
                          </div>
                          {editingRecord.original.aiRecommendations.gptAction && (
                            <div className="ai-field">
                              <span className="ai-field-label">Action:</span>
                              <span>{editingRecord.original.aiRecommendations.gptAction}</span>
                            </div>
                          )}
                          {editingRecord.original.aiRecommendations.gptTrend && (
                            <div className="ai-field">
                              <span className="ai-field-label">Trend:</span>
                              <span>{editingRecord.original.aiRecommendations.gptTrend}</span>
                            </div>
                          )}
                          {editingRecord.original.aiRecommendations.gptPattern && (
                            <div className="ai-field">
                              <span className="ai-field-label">Pattern:</span>
                              <span>{editingRecord.original.aiRecommendations.gptPattern}</span>
                            </div>
                          )}
                          {editingRecord.original.aiRecommendations.gptMarketAnalysis && (
                            <div className="ai-field">
                              <span className="ai-field-label">Analysis:</span>
                              <span>{editingRecord.original.aiRecommendations.gptMarketAnalysis}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setEditingRecord(null)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleSaveEdit}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deletingRecord && (
        <div className="modal-overlay" onClick={() => setDeletingRecord(null)}>
          <div className="modal-dialog confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Record</h3>
              <button className="close-btn" onClick={() => setDeletingRecord(null)}>✕</button>
            </div>
            <div className="modal-content">
              <p>Are you sure you want to delete this record?</p>
              <div className="delete-preview">
                <strong>{deletingRecord.action === 'entry' ? 'ENTRY' : 'EXIT'} {deletingRecord.symbol}</strong>
                <span>${(deletingRecord.actualPrice ?? 0).toFixed(2)} x {deletingRecord.quantity}</span>
                <span className="delete-time">{formatTime(deletingRecord.timestamp)}</span>
              </div>
              <p className="warning">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setDeletingRecord(null)}>
                Cancel
              </button>
              <button className="btn-danger" onClick={() => handleDeleteRecord(deletingRecord)}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auto-Match Results Dialog */}
      {showMatchDialog && matchResult && (
        <div className="modal-overlay" onClick={() => setShowMatchDialog(false)}>
          <div className="modal-dialog match-results-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Auto-Match Results</h3>
              <button className="close-btn" onClick={() => setShowMatchDialog(false)}>✕</button>
            </div>
            <div className="modal-content">
              {/* Overall Stats */}
              <div className="match-stats">
                <h4>Matching Statistics</h4>
                <div className="stats-grid">
                  <div className="stat-box">
                    <span className="stat-number">{matchResult.stats.totalMatched}</span>
                    <span className="stat-label">Matched</span>
                  </div>
                  <div className="stat-box">
                    <span className="stat-number">{matchResult.stats.totalUnmatched}</span>
                    <span className="stat-label">Unmatched</span>
                  </div>
                  <div className="stat-box">
                    <span className="stat-number">{matchResult.stats.totalPairs}</span>
                    <span className="stat-label">Pairs</span>
                  </div>
                  <div className="stat-box success">
                    <span className="stat-number">${matchResult.stats.totalProfit}</span>
                    <span className="stat-label">Total P/L</span>
                  </div>
                </div>
                <div className="stats-grid">
                  <div className="stat-box">
                    <span className="stat-number">{matchResult.stats.wins}</span>
                    <span className="stat-label">Wins</span>
                  </div>
                  <div className="stat-box">
                    <span className="stat-number">{matchResult.stats.losses}</span>
                    <span className="stat-label">Losses</span>
                  </div>
                  <div className="stat-box">
                    <span className="stat-number">{matchResult.stats.winRate}%</span>
                    <span className="stat-label">Win Rate</span>
                  </div>
                  <div className="stat-box">
                    <span className="stat-number">${matchResult.stats.avgProfit}</span>
                    <span className="stat-label">Avg P/L</span>
                  </div>
                </div>
              </div>

              {/* AI Accuracy Analysis */}
              <div className="ai-analysis">
                <h4>AI Recommendation Accuracy</h4>
                <div className="ai-stats-grid">
                  <div className="ai-stat-card">
                    <div className="ai-stat-header">Claude</div>
                    <div className="ai-stat-body">
                      <div>Trades: {matchResult.aiAnalysis.claude.total}</div>
                      <div>Win Rate: <strong>{matchResult.aiAnalysis.claude.winRate}%</strong></div>
                      <div>Avg P/L: <strong>${matchResult.aiAnalysis.claude.avgProfit}</strong></div>
                    </div>
                  </div>
                  <div className="ai-stat-card">
                    <div className="ai-stat-header">GPT</div>
                    <div className="ai-stat-body">
                      <div>Trades: {matchResult.aiAnalysis.gpt.total}</div>
                      <div>Win Rate: <strong>{matchResult.aiAnalysis.gpt.winRate}%</strong></div>
                      <div>Avg P/L: <strong>${matchResult.aiAnalysis.gpt.avgProfit}</strong></div>
                    </div>
                  </div>
                  <div className="ai-stat-card highlight">
                    <div className="ai-stat-header">Both AI Consensus</div>
                    <div className="ai-stat-body">
                      <div>Trades: {matchResult.aiAnalysis.bothAI.total}</div>
                      <div>Win Rate: <strong>{matchResult.aiAnalysis.bothAI.winRate}%</strong></div>
                      <div>Avg P/L: <strong>${matchResult.aiAnalysis.bothAI.avgProfit}</strong></div>
                    </div>
                  </div>
                  <div className="ai-stat-card">
                    <div className="ai-stat-header">No AI (Manual)</div>
                    <div className="ai-stat-body">
                      <div>Trades: {matchResult.aiAnalysis.noAI.total}</div>
                      <div>Win Rate: <strong>{matchResult.aiAnalysis.noAI.winRate}%</strong></div>
                      <div>Avg P/L: <strong>${matchResult.aiAnalysis.noAI.avgProfit}</strong></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Paired Trades */}
              {matchResult.pairs.length > 0 && (
                <div className="pairs-section">
                  <div className="section-header-with-sort">
                    <h4>
                      Completed Trades (Entry + Exit)
                      <span className="count-badge">
                        {expandedPairs
                          ? `${matchResult.pairs.length} of ${matchResult.pairs.length}`
                          : `${Math.min(10, matchResult.pairs.length)} of ${matchResult.pairs.length}`
                        }
                      </span>
                    </h4>
                    <div className="sort-control">
                      <label>Sort by:</label>
                      <select
                        value={pairsSortBy}
                        onChange={(e) => setPairsSortBy(e.target.value)}
                        className="sort-select-small"
                      >
                        <option value="profit-desc">Profit (High to Low)</option>
                        <option value="profit-asc">Profit (Low to High)</option>
                        <option value="percent-desc">% Gain (High to Low)</option>
                        <option value="percent-asc">% Gain (Low to High)</option>
                        <option value="time-desc">Time (Newest First)</option>
                        <option value="time-asc">Time (Oldest First)</option>
                        <option value="symbol">Symbol (A-Z)</option>
                        <option value="duration">Duration (Longest First)</option>
                      </select>
                    </div>
                  </div>
                  <div className="pairs-list">
                    {(expandedPairs
                      ? sortPairs(matchResult.pairs, pairsSortBy)
                      : sortPairs(matchResult.pairs, pairsSortBy).slice(0, 10)
                    ).map((pair, index) => (
                      <div key={index} className={`pair-card ${pair.profit > 0 ? 'profit' : 'loss'}`}>
                        <div className="pair-header">
                          <strong>{pair.symbol}</strong>
                          <span className={pair.profit > 0 ? 'profit-text' : 'loss-text'}>
                            {pair.profit > 0 ? '+' : ''}${(pair.profit ?? 0).toFixed(2)} ({pair.profitPercent > 0 ? '+' : ''}{(pair.profitPercent ?? 0).toFixed(2)}%)
                          </span>
                        </div>
                        <div className="pair-details">
                          <span>Entry: ${(pair.entryPrice ?? 0).toFixed(2)}</span>
                          <span>→</span>
                          <span>Exit: ${(pair.exitPrice ?? 0).toFixed(2)}</span>
                          <span>•</span>
                          <span>{pair.quantity} shares</span>
                          <span>•</span>
                          <span>{pair.duration}</span>
                        </div>
                      </div>
                    ))}
                    {matchResult.pairs.length > 10 && (
                      <button
                        className="more-info-btn"
                        onClick={() => setExpandedPairs(!expandedPairs)}
                      >
                        {expandedPairs
                          ? 'Show less'
                          : `... and ${matchResult.pairs.length - 10} more`
                        }
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Unmatched Records */}
              {matchResult.unmatchedUI.length > 0 && (
                <div className="unmatched-section">
                  <h4>
                    Unmatched Decisions
                    <span className="count-badge warning">
                      {expandedUnmatched
                        ? `${matchResult.unmatchedUI.length} of ${matchResult.unmatchedUI.length}`
                        : `${Math.min(5, matchResult.unmatchedUI.length)} of ${matchResult.unmatchedUI.length}`
                      }
                    </span>
                  </h4>
                  <div className="unmatched-list">
                    {(expandedUnmatched ? matchResult.unmatchedUI : matchResult.unmatchedUI.slice(0, 5)).map((record) => (
                      <div key={record.id} className="unmatched-card">
                        <span>{record.action === 'entry' ? 'Entry' : 'Exit'} {record.symbol}</span>
                        <span>${(record.actualPrice ?? 0).toFixed(2)} x{record.quantity}</span>
                        <span>{formatTime(record.timestamp)}</span>
                        <span className="hint">Not found in broker data</span>
                      </div>
                    ))}
                    {matchResult.unmatchedUI.length > 5 && (
                      <button
                        className="more-info-btn"
                        onClick={() => setExpandedUnmatched(!expandedUnmatched)}
                      >
                        {expandedUnmatched
                          ? 'Show less'
                          : `... and ${matchResult.unmatchedUI.length - 5} more`
                        }
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              {applyingMatches && (
                <div className="apply-progress-info">
                  <div className="progress-bar-container">
                    <div
                      className="progress-bar-fill"
                      style={{
                        width: `${applyProgress.total > 0 ? (applyProgress.current / applyProgress.total) * 100 : 0}%`
                      }}
                    />
                  </div>
                  <span className="progress-percentage">
                    {applyProgress.total > 0
                      ? `${Math.round((applyProgress.current / applyProgress.total) * 100)}%`
                      : '0%'
                    }
                  </span>
                </div>
              )}
              <button
                className="btn-secondary"
                onClick={() => setShowMatchDialog(false)}
                disabled={applyingMatches}
              >
                Close
              </button>
              <button
                className="btn-primary"
                onClick={applyMatches}
                disabled={applyingMatches}
              >
                {applyingMatches
                  ? `Applying (${applyProgress.current}/${applyProgress.total})...`
                  : 'Apply Matches'
                }
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default TradingJournal;
